import type { StatusTone } from '@/components/ui/StatusPill';

/**
 * 虚拟卡生命周期状态机
 * 与后端 Vcc Card.status 严格保持一致 (PRD §2)
 */
export type CardLifecycleStatus =
  | 'PENDING' // 接收中（已下单待上游回调）
  | 'ACTIVE' // 已激活
  | 'INACTIVE' // 未激活
  | 'FROZEN' // 风控冻卡
  | 'CLOSING' // 销户处理中
  | 'CLOSED' // 已销户
  | 'REJECTED'; // 申请遭拒

export interface CardStatusMetaItem {
  label: string;
  tone: StatusTone;
  frozen?: boolean;
  closed?: boolean;
}

export const CardStatusMeta: Record<CardLifecycleStatus, CardStatusMetaItem> = {
  PENDING: { label: '接收中', tone: 'processing' },
  ACTIVE: { label: '已激活', tone: 'success' },
  INACTIVE: { label: '未激活', tone: 'warning' },
  FROZEN: { label: '风控冻结', tone: 'danger', frozen: true },
  CLOSING: { label: '销户处理中', tone: 'processing' },
  CLOSED: { label: '已销户', tone: 'default', closed: true },
  REJECTED: { label: '申请遭拒', tone: 'danger', closed: true },
};

/** 卡段（BIN）配置，自上游下发并本地缓存 */
export interface CardBin {
  bin: string;
  brand: 'VISA' | 'MASTERCARD' | 'UNIONPAY' | string;
  currency: 'USD';
  /** 单笔最高额度（整数美分）；0 表示上游不限制 */
  singleLimitCents: number;
  /** 单卡最高额度（整数美分）；0 表示上游不限制 */
  totalLimitCents: number;
  /** 是否支持重复充值 (true=常驻可充值, false=单次) */
  reloadable: boolean;
  /** 上游风控提示 */
  notice?: string;
}

/** 虚拟卡完整元数据 */
export interface VccCard {
  id: string;
  bin: string;
  brand: string;
  alias: string;
  maskedPan: string;
  expireMonth: number;
  expireYear: number;
  status: CardLifecycleStatus;
  reloadable: boolean;
  /** 卡总限额（整数美分） */
  totalLimitCents: number;
  /** 当前可用余额（整数美分） */
  availableBalanceCents: number;
  billingProfileId: string;
  /** 最近一笔交易记账时间；无交易时为 null */
  lastUsedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 卡片敏感明文（解密接口返回） */
export interface CardSecret {
  cardId: string;
  pan: string;
  cvv: string;
  /** 明文有效期（秒）。前端在 TTL 内可缓存，到期后必须重新二次验证 */
  ttlSec: number;
}
